import { forwardRef, useEffect, useState } from "react";

import { If } from "@components/control-statements";

import styles from "@styles/pages/flow/FlowPanel.module.scss";

import { BoxCategories } from "@boxes/BoxCategories";

import { GodMode } from "@core/GodMode";
import classnames from "classnames/bind";
import ROLES from "data/Roles";
import { Environment } from "shared/core/Environment";
import { UserStorage } from "shared/tools/UserStorage";
import { BoxTypes } from "../boxesOraculo/BoxTypes";
const css = classnames.bind(styles);

const Group = ({ title, id, active, setActive, disabled }) => {
  return (
    <div
      onClick={() => (!disabled ? setActive(id) : null)}
      className={css({ groupHeader: true, active: id === active })}
    >
      <If cond={disabled === true}>
        <i className="far fa-lock" />
      </If>
      <span>{title}</span>
    </div>
  );
};

const BoxGroup = ({ title, disabled, onDragStart }) => {
  return (
    <div
      className={styles.boxGroup}
      onDragStart={(...args) => !disabled && onDragStart(...args)}
      draggable={!disabled}
    >
      <i className="far fa-layer-group" />
      <span className={styles.title}>{title}</span>
    </div>
  );
};

const Box = ({ title, disabled, onDragStart }) => {
  return (
    <div
      className={styles.box}
      onDragStart={(...args) => !disabled && onDragStart(...args)}
      draggable={!disabled}
    >
      <span className={styles.title}>{title}</span>
    </div>
  );
};

const FlowPanel = forwardRef(({ onCollapse, restrictions, boxes }, ref) => {
  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  const [active, setActive] = useState(0);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    onCollapse && onCollapse(ref.current);
  }, [collapsed, active]);

  const isLockedGroup = ({ restrictions, group }) => {
    if (group?.label === "Filtros" && restrictions?.lockFilters) {
      return true;
    }

    if (group?.label === "Condicionais" && restrictions?.lockConditionals) {
      return true;
    }
  };

  const isLockedBox = ({ restrictions, boxType }) => {
    return !!restrictions?.[`lock${boxType}`];
  };

  const isCocoBambu = Environment.id === 49 || Environment.id === 51 || Environment.id === 29;
  const isBroadcastFlow = (boxes || []).some((box) => box.type === BoxTypes.BroadcastAudience);

  const lockedPanel = restrictions?.lockFlowPanel;

  let userRole = UserStorage.getLoggedUser()?.role;

  return (
    <div
      className={css({
        flowPanel: true,
        collapsed: collapsed && !lockedPanel,
        disabled: lockedPanel,
      })}
    >
      <div className={styles.tabsContainer}>
        <div className={styles.tabsHeader}>
          <div className={styles.groups} ref={ref}>
            {BoxCategories.map((category, _i) => (
              <Group
                title={category.label}
                id={_i}
                active={active}
                setActive={(label) => {
                  if (lockedPanel) return;
                  setCollapsed(true);
                  setActive(label);
                }}
                disabled={isLockedGroup({ restrictions, group: category })}
              />
            ))}
          </div>

          <div
            className={css({ collapse: true, active: collapsed })}
            onClick={() => !lockedPanel && setCollapsed(!collapsed)}
          >
            <i className="fas fa-chevron-up"></i>
          </div>
        </div>

        {BoxCategories.map(
          (category, _i) =>
            active === _i && (
              <div
                className={css({ panelContent: true, collapsed: collapsed })}
              >
                <div className={styles.boxes}>
                  {category.boxes
                    .filter((box) => box.isGroup)
                    .map((box) => (
                      <BoxGroup
                        title={box.label}
                        disabled={
                          box.disabled ||
                          isLockedBox({ restrictions, boxType: box.type })
                        }
                        onDragStart={(event) => onDragStart(event, box.type)}
                      />
                    ))}

                  {category.boxes
                    .filter(
                      (box) =>
                        !box.isGroup &&
                        !box.hide &&
                        (box.godmode ? GodMode.isGodMode : true) &&
                        (box.type === BoxTypes.BroadcastAudience ? isCocoBambu : true) && 
                        (box.type === BoxTypes.SpecificClimateConditional && isBroadcastFlow ? false : true)
                    )
                    .map((box) => {
                      if (box.type === "ImportedAudience" && userRole !== ROLES.SUPPORT) return null
                      
                      return (
                        <Box
                          title={box.label}
                          disabled={
                            box.disabled ||
                            isLockedBox({ restrictions, boxType: box.type })
                          }
                          onDragStart={(event) => onDragStart(event, box.type)}
                        />   
                      )
                    })}
                </div>
              </div>
            )
        )}
      </div>
    </div>
  );
});

export default FlowPanel;
